/* TNZ canvas renderer v1. Only normalized image/text manifests; no source-site runtime. */
(() => {
  if (window.TnzCustomPreview) return;
  const fontCache = new Map();
  function assetUrl(manifest, id) {
    const value = manifest.assets[id]?.url;
    const url = new URL(typeof value === 'string' && value.startsWith('//') ? `https:${value}` : value, location.href);
    if (!value || (url.protocol !== 'https:' && url.origin !== location.origin)) throw new Error('Invalid preview asset.');
    return url.href;
  }
  function image(manifest, id, imageCache) {
    const url = assetUrl(manifest, id);
    if (!imageCache.has(url)) imageCache.set(url, new Promise((resolve, reject) => {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img); img.onerror = () => { imageCache.delete(url); reject(new Error('Không tải được lớp ảnh preview.')); }; img.src = url;
    }));
    return imageCache.get(url);
  }
  function font(manifest, id) {
    const family = `TnzFont${id.slice(0, 20)}`;
    const url = assetUrl(manifest, id);
    if (!fontCache.has(url)) fontCache.set(url, new FontFace(family, `url(${JSON.stringify(url)})`).load().then(face => {
      document.fonts.add(face); return family;
    }).catch(error => { fontCache.delete(url); throw error; }));
    return fontCache.get(url);
  }
  function visibleFields(manifest, values = {}, includeDisabled = false) {
    const fields = new Map(manifest.fields.map(field => [field.id, field]));
    const result = {}; const visiting = new Set();
    function visible(id) {
      if (id in result) return result[id];
      const field = fields.get(id);
      if (!field || (!includeDisabled && field.enabled === false) || visiting.has(id)) return false;
      visiting.add(id);
      let show = true;
      if (field.visibility) {
        let matched = false;
        field.visibility.rules.forEach((rule, index) => {
          const watched = fields.get(rule.field);
          const selected = String(values[rule.field] ?? watched?.choices?.[0]?.id ?? '');
          const valid = watched?.choices?.some(choice => choice.id === selected);
          const match = !!valid && visible(rule.field) && (rule.values.includes('-1') || rule.values.includes(selected));
          matched = index === 0 ? match : rule.join === 'and' ? matched && match : matched || match;
        });
        show = field.visibility.action === 'hide' ? !matched : matched;
      }
      visiting.delete(id); return result[id] = show;
    }
    for (const id of fields.keys()) visible(id);
    return result;
  }
  function setFieldValue(manifest, values, id, value) {
    const field = manifest.fields.find(item => item.id === id);
    values[id] = String(value);
    if (!field || field.type !== 'text' || field.enabled === false) return;
    const peers = manifest.fields.filter(item => item.type === 'text' && item.enabled !== false && item.name === field.name);
    if (peers.length < 2 || peers.some(item => item.max_length !== field.max_length)) return;
    let watched;
    const branches = [];
    for (const peer of peers) {
      const visibility = peer.visibility;
      if (!visibility || visibility.rules.length !== 1 || !['show','hide'].includes(visibility.action)) return;
      const rule = visibility.rules[0];
      const control = manifest.fields.find(item => item.id === rule.field);
      if (!control || control.type !== 'select' || control.visibility || (watched && watched !== control.id)) return;
      watched = control.id;
      const choices = new Set(control.choices.filter(choice => {
        const match = rule.values.includes('-1') || rule.values.includes(choice.id);
        return visibility.action === 'hide' ? !match : match;
      }).map(choice => choice.id));
      if (!choices.size || branches.some(previous => [...choices].some(choice => previous.has(choice)))) return;
      branches.push(choices);
    }
    // Only mutually exclusive presentations of the same input share text.
    // Simultaneously visible duplicate labels keep their independent values.
    for (const peer of peers) values[peer.id] = String(value);
  }
  function create(canvas, manifest) {
    if (manifest.schema !== 1 || !manifest.width || !manifest.height) throw new Error('Unsupported preview package.');
    const imageCache = new Map();
    let sequence = 0;
    let disposed = false;
    const defaults = Object.fromEntries(manifest.fields.map(field => [field.id, field.type === 'select' ? field.choices[0]?.id ?? '' : '']));
    canvas.width = 2000; canvas.height = Math.round(2000 * manifest.height / manifest.width);
    const context = canvas.getContext('2d');
    async function draw(values = {}) {
      const request = ++sequence;
      const selected = { ...defaults, ...values };
      for (const field of manifest.fields) if (field.enabled === false) selected[field.id] = defaults[field.id];
      const visible = visibleFields(manifest, selected, true);
      const layers = manifest.layers.filter(layer => !layer.field || visible[layer.field]).sort((a, b) => a.z - b.z);
      const resources = new Map();
      await Promise.all([image(manifest, manifest.background, imageCache).then(img => resources.set(manifest.background, img)), ...layers.map(async layer => {
        if (layer.type === 'image') {
          const id = layer.sources[layer.field ? selected[layer.field] : 'default'];
          if (!id) throw new Error('Thiếu ảnh cho lựa chọn preview.');
          resources.set(id, await image(manifest, id, imageCache));
        } else if (layer.type === 'text') resources.set(layer.font, await font(manifest, layer.font));
        else throw new Error('Unsupported preview layer.');
      })]);
      if (disposed || request !== sequence) return false;
      context.setTransform(1, 0, 0, 1, 0, 0); context.clearRect(0, 0, canvas.width, canvas.height);
      context.scale(canvas.width / manifest.width, canvas.height / manifest.height);
      context.imageSmoothingEnabled = true; context.imageSmoothingQuality = 'high';
      context.drawImage(resources.get(manifest.background), 0, 0, manifest.width, manifest.height);
      for (const layer of layers) {
        context.save(); context.globalAlpha = layer.opacity ?? 1;
        context.translate(layer.x, layer.y); context.rotate(layer.rotation * Math.PI / 180);
        context.scale(layer.flip_x ? -1 : 1, layer.flip_y ? -1 : 1);
        context.transform(1, 0, Math.tan((layer.skew_x ?? 0) * Math.PI / 180), 1, 0, 0);
        context.transform(1, Math.tan((layer.skew_y ?? 0) * Math.PI / 180), 0, 1, 0, 0);
        if (layer.type === 'image') {
          const img = resources.get(layer.sources[layer.field ? selected[layer.field] : 'default']);
          const ratio = Math.min(layer.width / img.naturalWidth, layer.height / img.naturalHeight);
          const width = img.naturalWidth * ratio, height = img.naturalHeight * ratio;
          context.drawImage(img, -width / 2, -height / 2, width, height);
        } else {
          const field = manifest.fields.find(f => f.id === layer.field);
          let text = String(selected[layer.field] ?? '').slice(0, field?.max_length ?? 100);
          if (layer.caps) text = text.toUpperCase();
          if (text.trim()) {
            text = `${layer.prefix ?? ''}${text}${layer.suffix ?? ''}`;
            let size = layer.size;
            const family = resources.get(layer.font);
            context.font = `${size}px "${family}"`;
            let metrics = context.measureText(text);
            const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
            const ratio = Math.min(1, layer.width / Math.max(metrics.width, 1), layer.height / Math.max(textHeight, 1));
            size *= ratio; context.font = `${size}px "${family}"`;
            metrics = context.measureText(text);
            context.fillStyle = layer.color; context.textAlign = 'center'; context.textBaseline = 'alphabetic';
            context.fillText(text, 0, (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2);
          }
        }
        context.restore();
      }
      return true;
    }
    return { draw, defaults, destroy() { disposed = true; sequence++; imageCache.clear(); } };
  }
  window.TnzCustomPreview = { create, visibleFields, setFieldValue, version: 1 };
})();
